import { useFeatureFlag } from "@common/api/feature-flags"
import {
  findHubSpot,
  findSalesforce,
  hubSpotConfigQuery,
  hubSpotEligibilityQuery,
  hubSpotPropertyMappingQuery,
  tenantIntegrationsQuery,
} from "@common/api/tenant-integrations"
import { Skeleton } from "@common/components/ui/skeleton"
import { SALESFORCE } from "@common/flags"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Suspense } from "react"
import { PageContent, PageTitle } from "@/components/page/page-content"
import useCustomToast from "@/hooks/use-custom-toast"
import { requirePermission } from "@/lib/require-permission"
import { HubSpotConnection } from "./crm/-components/connection"
import { HubSpotQuotingRules } from "./crm/-components/quoting-rules"
import { SalesforceConnection } from "./crm/-components/salesforce-connection"

export const Route = createFileRoute(
  "/_layout/settings/_panel/integrations/crm",
)({
  staticData: { breadcrumb: [{ label: "Integrations" }, { label: "CRM" }] },
  beforeLoad: ({ context: { queryClient } }) =>
    requirePermission(
      queryClient,
      "tenant_integration_config:read",
      "/settings/workspace/general",
    ),
  loader: async ({ context: { queryClient } }) => {
    const configs = await queryClient.ensureQueryData(tenantIntegrationsQuery())
    const config = findHubSpot(configs)
    if (config === undefined) return
    const params = { config_id: config.id }
    await Promise.all([
      queryClient.ensureQueryData(hubSpotConfigQuery(params)),
      queryClient.ensureQueryData(hubSpotEligibilityQuery(params)),
      queryClient.ensureQueryData(hubSpotPropertyMappingQuery(params)),
    ])
  },
  component: CrmPage,
})

function CrmPage(): React.ReactElement {
  const { data: configs } = useSuspenseQuery(tenantIntegrationsQuery())
  const config = findHubSpot(configs)
  const showToast = useCustomToast()
  const salesforce = useFeatureFlag(SALESFORCE) ?? false

  return (
    <PageContent>
      <PageTitle title="CRM" />
      <HubSpotConnection config={config} />
      {config && (
        <Suspense fallback={<Skeleton className="h-64 w-full" />}>
          <HubSpotQuotingRules
            config={config}
            onSave={() =>
              showToast("Saved", "HubSpot quoting rules updated.", "success")
            }
          />
        </Suspense>
      )}
      {salesforce && <SalesforceConnection config={findSalesforce(configs)} />}
    </PageContent>
  )
}
